import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { localeLanguageCodes } from 'shared-locales';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import { EmptyIndicator } from 'components/common';
import { Box } from 'components/common/box';
import {
  ProfileCourseChapters,
  ProfileCourseInfo,
  ProfileCoursePreviousCompletions,
  ProfileCourseProgress,
  ProfileCoursesCourseTasks
} from '.';

import type { GetUserCoursesCourseRes } from 'shared-types/responses';

interface Props {
  courseId: number;
  isUserPage?: boolean;
}

const ProfileCoursesCourse = ({ courseId, isUserPage }: Props) => {
  const {
    language,
    messages,
    pageState: { user },
    user: loggedInUser
  } = useReduxState();

  const { query } = useRouter();

  const { data, loading, updateData } = useApiData<GetUserCoursesCourseRes>({
    url: `/admin/users/${user.id}/courses/${courseId}`
  });

  const availableLanguages = getCourseLanguageVersionOptions({
    course: data?.course
  });

  const defaultLanguageVersion = useMemo(
    () =>
      typeof query.languageVersion === 'string'
        ? query.languageVersion
        : data?.progressData?.completion?.courseLanguageVersion ||
          localeLanguageCodes[language],
    [
      data?.progressData?.completion?.courseLanguageVersion,
      language,
      query.languageVersion
    ]
  );

  const displayLanguageVersion =
    availableLanguages[defaultLanguageVersion] &&
    availableLanguages[defaultLanguageVersion].isPublished
      ? defaultLanguageVersion
      : data?.course?.language;

  return (
    <div className="summary">
      <div className="side">
        <ProfileCourseInfo
          course={data?.course}
          courseCompletionLanguage={
            data?.progressData?.completion?.courseLanguageVersion
          }
          languageVersion={displayLanguageVersion}
          isUserPage={isUserPage}
          loading={loading}
        />
      </div>

      <div className="main">
        <Box loading={loading} padding={20} title={messages.commonProgress}>
          {data?.course ? (
            <div className="course">
              <div className="summary">
                <ProfileCourseProgress
                  allowDownloadingCertificate={
                    isUserPage || data?.course?.certificateAllowDownloading
                  }
                  course={data?.course}
                  deadline={data?.course?.deadline}
                  lastOpenedDate={data?.course?.lastOpenedDate}
                  progressData={data?.progressData}
                  relatedPathDeadlines={data?.course?.relatedPathDeadlines}
                  userId={user.id}
                />
              </div>
            </div>
          ) : (
            <EmptyIndicator
              description={messages.commonAdminPermissionsInsufficientAnalytics}
              icon="lock"
              title={messages.errorForbidden}
            />
          )}
        </Box>

        {data?.previousCourseCompletions?.length ? (
          <div className="previous-completions">
            <ProfileCoursePreviousCompletions
              certificateAllowDownloading={
                data?.course?.certificateAllowDownloading
              }
              completionsAllowViewing={
                !isUserPage ||
                data?.course?.isOwnerOrSuperadmin ||
                (data?.course?.isAdmin &&
                  data?.course?.adminPermissions?.includes('review-tasks'))
              }
              courseLanguageVersions={data?.course?.languageVersions}
              courseSlug={data?.course?.slug}
              courseVersionTagsByVersionNumber={
                data?.courseVersionTagsByVersionNumber
              }
              isUserPage={isUserPage}
              previousCourseCompletions={data?.previousCourseCompletions}
              tasksAllowDownloading={data?.course?.tasksAllowDownloading}
              tasksIncludeReviews={data?.course?.tasksIncludeReviews}
              updateUser={updateData}
              user={user}
            />
          </div>
        ) : null}

        <div className="chapters">
          <ProfileCourseChapters
            chapters={data?.chapters}
            languageVersion={displayLanguageVersion}
            progressData={data?.progressData}
          />
        </div>

        <div className="tasks">
          {data?.accessToTaskData ? (
            <ProfileCoursesCourseTasks
              chapters={data?.chapters}
              course={data?.course}
              isUserPage={isUserPage}
              loading={loading}
              loggedUserHasEmail={Boolean(loggedInUser?.email)}
              onDataChange={updateData}
              tasks={data?.tasks}
              tasksAllowDownloading={
                data?.course ? data.course.tasksAllowDownloading : false
              }
              tasksOrder={data?.tasksOrder}
              user={user}
              userTasksData={data?.tasksData}
            />
          ) : (
            <Box
              loading={loading}
              padding={0}
              style={{ marginTop: 25 }}
              title={messages.tasksAnswers}
            >
              <EmptyIndicator
                description={messages.commonAdminPermissionsInsufficientAnswers}
                icon="lock"
                title={messages.errorForbidden}
              />
            </Box>
          )}
        </div>
      </div>

      <style jsx>{`
        .summary {
          display: flex;
          flex-direction: column;
          gap: 30px 25px;
        }
        .previous-completions,
        .chapters,
        .tasks {
          margin-top: 30px;
        }
        @media (min-width: ${breakpoints.large}px) {
          .summary {
            flex-direction: row;
          }
          .side {
            flex-shrink: 0;
            width: 360px;
          }
          .main {
            flex-grow: 1;
            min-width: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileCoursesCourse;
